import * as _ from 'lodash';
import { Component, ViewChild, ElementRef, inject, OnInit, AfterContentInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    BulkDownloadImagesInformation,
    BulkSearchResult,
    BulkSearchResultWithInformation
} from 'app/content/models/product.model';
import { combineLatest, fromEvent, Observable, Subject, BehaviorSubject, merge, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    withLatestFrom,
    switchMap,
    shareReplay,
    mapTo,
    filter,
    catchError
} from 'rxjs/operators';

import { Type } from 'fc-ui-components';

import { ofType, ImageResolution, platformAvailableLanguages } from 'helpers';
import { SegmentService, UserService, WindowService } from 'services';
import { DownloadInitiator } from 'services/tracking/download-initiator';
import { maxAmountOfArticleNumbers, maxAmountOfGtins } from '../constants';
import { ProductService } from 'app/content/services/product.service';
import { VideoDeliveryFeatureService } from 'app/content/services/video-delivery-feature.service';
import {
    BulkDownloadToastSearchBy,
    BulkDownloadToastSelection,
    BulkDownloadToastService,
    isBulkDownloadTooManyGtinsError,
    withBulkDownloadCsvEmptyResponseInformation
} from 'app/content/services/bulk-download-toast.service';

type BulkDownloadStreamResult = (BulkSearchResult | BulkSearchResultWithInformation) & {
    selection?: BulkDownloadToastSelection;
    bulkDownloadFailed?: boolean;
    bulkDownloadSearchBy?: BulkDownloadToastSearchBy;
};

@Component({
    selector: 'fc-bulk-gtin-search',
    templateUrl: './bulk-search.component.html',
    styleUrls: ['./bulk-search.component.less'],
    standalone: false
})
export class BulkGtinSearchComponent implements OnInit, AfterContentInit {
    @ViewChild('input', { static: true }) private input: ElementRef;

    public maxGtins = maxAmountOfGtins;
    public maxArticleNumbers = maxAmountOfArticleNumbers;
    public itemsLimitValidation: Observable<{ showNotification: boolean; lastItem: string }>;
    public itemsFormatValidation: Observable<{
        showNotificationAllInvalid: boolean;
        showNotificationSomeInvalid: boolean;
        invalidGtinsSample: string[];
        invalidGtinsAmount: number;
        totalGtinsAmount: number;
    }>;
    public isInputValid: Observable<boolean>;
    public noImagesFound: Observable<boolean>;
    public missingImagesInDownload: Observable<boolean | BulkDownloadImagesInformation>;
    public missingProductDataInDownload: Observable<boolean | BulkDownloadImagesInformation>;
    public isErrorMessageVisible: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public downloadInformation: Observable<boolean | BulkDownloadImagesInformation>;
    public downloadTrigger: Subject<{ type: DownloadEventType }> = new Subject();
    public selectedResolution: ImageResolution;
    public selectedCsvLanguage: string;
    public selectedSearchType: BehaviorSubject<SearchType>;
    public isGtinsSearch: Observable<boolean>;
    public isArticleNumbersSearch: Observable<boolean>;
    public searchType = SearchType;
    public downloadEventType = DownloadEventType;
    public selectedCsvDownloadType = false;
    public selectedImageDownloadType = true;
    public selectedVideoDownloadType = false;
    public readonly videoDownloadEnabled = inject(VideoDeliveryFeatureService).enabled;
    public readonly resolutionOptions = [
        { value: ImageResolution.Original, text: 'DOWNLOADS.SIZE.ORIGINAL' },
        { value: ImageResolution.Large, text: 'DOWNLOADS.SIZE.LARGE' },
        { value: ImageResolution.Medium, text: 'DOWNLOADS.SIZE.MEDIUM' },
        { value: ImageResolution.Thumb, text: 'DOWNLOADS.SIZE.SMALL' }
    ];
    public csvLanguageOptions;
    public readonly alertTypeEnum = Type;

    constructor(
        public route: ActivatedRoute,
        private productService: ProductService,
        private userService: UserService,
        private windowService: WindowService,
        private segmentService: SegmentService,
        private bulkDownloadToastService: BulkDownloadToastService
    ) {
        this.selectedResolution = ImageResolution.Original;
        this.selectedCsvLanguage = CsvLanguageOption.UserPreferredLanguage;
        this.selectedSearchType = new BehaviorSubject(SearchType.ARTICLE_NUMBERS);
    }

    ngOnInit() {
        this.isGtinsSearch = this.selectedSearchType.pipe(map((searchType) => searchType === SearchType.GTINS));

        this.isArticleNumbersSearch = this.selectedSearchType.pipe(
            map((searchType) => searchType === SearchType.ARTICLE_NUMBERS)
        );

        this.csvLanguageOptions = [
            {
                value: CsvLanguageOption.UserPreferredLanguage,
                text: platformAvailableLanguages[this.userService.getPreferredLanguage()].name,
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.PREFERRED_LANGUAGE.HINT'
            },
            {
                value: CsvLanguageOption.All,
                text: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.TEXT',
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.HINT'
            }
        ];
    }

    ngAfterContentInit() {
        const input: Observable<string> = fromEvent(this.input.nativeElement, 'input').pipe(
            map((event: any) => event.target.value),
            debounceTime(500),
            distinctUntilChanged()
        );

        const parsedItems: Observable<{ items: string[]; searchType: SearchType }> = combineLatest([
            input,
            this.selectedSearchType
        ]).pipe(
            map(([inputValue, searchType]) => ({
                searchType,
                items: _.filter(_.split(inputValue, /[,;\n]+/))
            })),
            shareReplay()
        );

        const sortedItems: Observable<{ items: { valid: string[]; invalid: string[] }; searchType: SearchType }> =
            parsedItems.pipe(
                map(({ items, searchType }) => {
                    const slicedItems = _.slice(items, 0, this.getMaxAmountLimit(searchType));

                    return {
                        searchType,
                        items:
                            searchType === SearchType.GTINS
                                ? this.productService.validateGtins(slicedItems)
                                : {
                                      valid: slicedItems,
                                      invalid: []
                                  }
                    };
                })
            );

        this.itemsLimitValidation = parsedItems.pipe(
            map(({ items, searchType }) => {
                const maxAmount = this.getMaxAmountLimit(searchType);
                const showNotification = _.size(items) > maxAmount;
                const lastItem = showNotification ? items[maxAmount - 1] : '';

                return { showNotification, lastItem };
            })
        );

        this.itemsFormatValidation = sortedItems.pipe(
            map(({ items: { valid, invalid } }) => ({
                showNotificationAllInvalid: _.isEmpty(valid) && !_.isEmpty(invalid),
                showNotificationSomeInvalid: !_.isEmpty(valid) && !_.isEmpty(invalid),
                invalidGtinsSample: _.slice(invalid, 0, 3),
                invalidGtinsAmount: _.size(invalid),
                totalGtinsAmount: _.size(valid) + _.size(invalid)
            }))
        );

        this.isInputValid = combineLatest([this.itemsFormatValidation, parsedItems]).pipe(
            map(([{ showNotificationAllInvalid }, { items }]) => !showNotificationAllInvalid && !_.isEmpty(items))
        );

        const downloadCsv = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_CSV),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_CSV)
                })
            ),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkDownload({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        userLanguage: this.userService.getPreferredLanguage(),
                        size: this.selectedResolution,
                        csvLanguage: this.selectedCsvLanguage,
                        initiator: DownloadInitiator.BulkDownload
                    })
                    .pipe(
                        map((result) =>
                            withBulkDownloadCsvEmptyResponseInformation(
                                result,
                                this.toBulkDownloadSearchBy(searchType),
                                validItems.length
                            )
                        ),
                        catchError((error) => {
                            console.error('CSV download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);

                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        const downloadImages = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_IMAGES),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_IMAGES)
                })
            ),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkImagesDownload({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        size: this.selectedResolution,
                        initiator: DownloadInitiator.BulkDownload,
                        images: selection.images,
                        video: selection.video
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('Images download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);

                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        const downloadImagesWithCsv = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV)
                })
            ),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkImagesDownloadWithCsv({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        userLanguage: this.userService.getPreferredLanguage(),
                        size: this.selectedResolution,
                        csvLanguage: this.selectedCsvLanguage,
                        initiator: DownloadInitiator.BulkDownload,
                        images: selection.images,
                        video: selection.video
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('Images with CSV download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);

                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        merge(downloadImages, downloadCsv, downloadImagesWithCsv).subscribe({
            next: (result: BulkDownloadStreamResult) => {
                void this.bulkDownloadToastService
                    .handleBulkDownloadResponse(result, () => {
                        this.windowService.location = result.downloadUrl;
                    })
                    .catch((error) => console.error('Bulk download toast failed', error));
            },
            error: (error) => {
                console.error('Download error:', error);
                void this.bulkDownloadToastService
                    .handleBulkDownloadResponse(
                        { downloadUrl: '', tooManyGtins: false, bulkDownloadFailed: true },
                        () => undefined
                    )
                    .catch((toastError) => console.error('Bulk download toast failed', toastError));
            }
        });

        this.downloadInformation = merge(
            merge(downloadCsv, downloadImages, downloadImagesWithCsv).pipe(
                filter(({ tooManyGtins }) => !tooManyGtins),
                filter(this.isBulkSearchResultWithInformation),
                map(({ downloadInformation }) => downloadInformation),
                distinctUntilChanged(),
                shareReplay(1)
            ),
            this.selectedSearchType.pipe(mapTo(false)),
            this.isErrorMessageVisible.pipe(mapTo(false))
        );

        this.noImagesFound = merge(
            merge(downloadImages, downloadImagesWithCsv).pipe(
                filter(({ tooManyGtins }) => !tooManyGtins),
                map(({ downloadUrl }) => _.isEmpty(downloadUrl))
            ),
            this.selectedSearchType.pipe(mapTo(false)),
            this.isErrorMessageVisible.pipe(mapTo(false))
        );

        this.missingImagesInDownload = this.downloadInformation.pipe(
            filter(this.isBulkDownloadImagesInformation),
            map((downloadInformation) => (downloadInformation?.notFoundImages > 0 ? downloadInformation : false))
        );

        this.missingProductDataInDownload = this.downloadInformation.pipe(
            filter(this.isBulkDownloadImagesInformation),
            map((downloadInformation) => (downloadInformation?.notFound > 0 ? downloadInformation : false))
        );
    }

    public onClickDownload() {
        this.segmentService.track('Product data bulk search', {
            imageDownloadType: this.selectedImageDownloadType,
            videoDownloadType: this.selectedVideoDownloadType && this.videoDownloadEnabled(),
            csvDownloadType: this.selectedCsvDownloadType,
            selectedImageQuality: this.selectedResolution
        });
        this.isErrorMessageVisible.next(false);

        if (this.hasMediaSelection && this.selectedCsvDownloadType) {
            return this.downloadImagesWithCsv();
        }
        if (this.hasMediaSelection) {
            return this.downloadImages();
        }
        if (this.selectedCsvDownloadType) {
            return this.downloadProductData();
        }
    }

    public onResolutionSelected(imageResolution: ImageResolution) {
        this.selectedResolution = imageResolution;
    }

    public onCsvLanguageSelected(languageOption: string) {
        this.selectedCsvLanguage = languageOption;
    }

    public onDownloadTypeSelected(value: boolean, downloadType: DownloadEventType) {
        if (downloadType === DownloadEventType.DOWNLOAD_CSV) {
            this.selectedCsvDownloadType = value;
        }
        if (downloadType === DownloadEventType.DOWNLOAD_IMAGES) {
            this.selectedImageDownloadType = value;
        }
        if (downloadType === DownloadEventType.DOWNLOAD_VIDEOS) {
            this.selectedVideoDownloadType = value;
        }
    }

    public get hasMediaSelection(): boolean {
        return this.selectedImageDownloadType || (this.selectedVideoDownloadType && this.videoDownloadEnabled());
    }

    public get isDownloadDisabled(): boolean {
        return !this.hasMediaSelection && !this.selectedCsvDownloadType;
    }

    public changeSearchType(searchType: SearchType) {
        this.selectedSearchType.next(searchType);
    }

    private downloadImages() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_IMAGES });
    }

    private downloadImagesWithCsv() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV });
    }

    private downloadProductData() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_CSV });
    }

    private getMaxAmountLimit(searchType: SearchType) {
        return searchType === SearchType.GTINS ? this.maxGtins : this.maxArticleNumbers;
    }

    private isBulkDownloadImagesInformation(
        searchResult: BulkDownloadImagesInformation | boolean
    ): searchResult is BulkDownloadImagesInformation {
        return (searchResult as BulkDownloadImagesInformation).requested !== undefined;
    }

    private isBulkSearchResultWithInformation(
        searchResult: BulkSearchResult | BulkSearchResultWithInformation
    ): searchResult is BulkSearchResultWithInformation {
        return (searchResult as BulkSearchResultWithInformation).downloadInformation !== undefined;
    }

    private toBulkDownloadSearchBy(searchType: SearchType): BulkDownloadToastSearchBy {
        return searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers';
    }

    private snapshotDownloadSelection(eventType: DownloadEventType): BulkDownloadToastSelection {
        const video = this.selectedVideoDownloadType && this.videoDownloadEnabled();

        switch (eventType) {
            case DownloadEventType.DOWNLOAD_CSV:
                return { images: false, video: false, csv: this.selectedCsvDownloadType };
            case DownloadEventType.DOWNLOAD_IMAGES:
                return { images: this.selectedImageDownloadType, video, csv: false };
            case DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV:
                return { images: this.selectedImageDownloadType, video, csv: this.selectedCsvDownloadType };
            default:
                return { images: this.selectedImageDownloadType, video, csv: this.selectedCsvDownloadType };
        }
    }
}

enum DownloadEventType {
    DOWNLOAD_CSV = 'DOWNLOAD_CSV',
    DOWNLOAD_IMAGES = 'DOWNLOAD_IMAGES',
    DOWNLOAD_IMAGES_WITH_CSV = 'DOWNLOAD_IMAGES_WITH_CSV',
    DOWNLOAD_VIDEOS = 'DOWNLOAD_VIDEOS'
}

export enum SearchType {
    ARTICLE_NUMBERS = 'article_numbers',
    GTINS = 'gtins'
}

export enum CsvLanguageOption {
    UserPreferredLanguage = 'preferredLanguage',
    All = 'all'
}
